import { t } from "i18next";
import moment from "moment";

import { API_DATE_FORMAT } from "@/src/constants/dateFormat";
import prettyDurations, { DURATION_UNITS } from "@/src/utils/durations";

import type { ContentView } from "@/src/api/StatsAPI.types";
import type { ChartConfig } from "@/src/components/ui/chart";

import type {
  ChartCardStatus,
  ChartSeries,
  ChartValueUnit,
  RankedDatum,
  RoundAxis,
  TimelineAggregation,
  TimelineDatum,
  TimelineGranularity,
  ValueAxisConfig,
  ViewsSeriesKey,
} from "./charts.types";

export const RANKED_DEFAULT_LIMIT = 8;
const MAX_FILLED_DAYS = 366;
const MAX_DAY_GRANULARITY_DAYS = 92;
const MAX_WEEK_GRANULARITY_DAYS = 731;
const MONTH_KEY_FORMAT = "YYYY-MM-01";
const CATEGORY_AXIS_CHAR_WIDTH = 7;
const CATEGORY_AXIS_PADDING = 16;
export const CATEGORY_AXIS_MIN_WIDTH = 48;
export const CATEGORY_AXIS_MAX_WIDTH = 168;

// API dates may carry a time part; charts work per day.
export function toDay(date: string): string {
  return date.slice(0, 10);
}

export function buildViewsTimeline(
  viewList: readonly ContentView[],
): TimelineDatum<ViewsSeriesKey>[] {
  const viewsByDate = new Map(
    viewList.map((view) => [toDay(view.date), view.views]),
  );
  return [...viewsByDate].map(([date, views]) => ({
    date,
    values: { views },
  }));
}

export function mapSeriesValues<TKey extends string>(
  keyList: readonly TKey[],
  getValue: (key: TKey) => number,
): Record<TKey, number> {
  // Object.fromEntries widens keys to string; they are exactly keyList.
  return Object.fromEntries(
    keyList.map((key) => [key, getValue(key)]),
  ) as Record<TKey, number>;
}

export function sumSeriesValues<TKey extends string>(
  values: Record<TKey, number>,
  keyList: readonly TKey[],
): number {
  return keyList.reduce((sum, key) => sum + values[key], 0);
}

export function getLastNonZeroKey<TKey extends string>(
  values: Record<TKey, number>,
  keyList: readonly TKey[],
): TKey | undefined {
  return keyList.findLast((key) => values[key] > 0);
}

export function hasSeriesData<TKey extends string>(
  dataList: readonly { values: Record<TKey, number> }[],
  keyList: readonly TKey[],
): boolean {
  return dataList.some((datum) => sumSeriesValues(datum.values, keyList) > 0);
}

export function fillMissingDates<TKey extends string>(
  dataList: readonly TimelineDatum<TKey>[],
  keyList: readonly TKey[],
  startDate: string,
  endDate: string,
): TimelineDatum<TKey>[] {
  if (!dataList.length) return [];

  const valuesByDate = new Map(
    dataList.map((datum) => [datum.date, datum.values]),
  );
  const emptyValues = mapSeriesValues(keyList, () => 0);
  const filledList: TimelineDatum<TKey>[] = [];
  const end = moment(endDate, API_DATE_FORMAT);
  // An open-ended start (e.g. 1970) must not turn into thousands of empty bars.
  const isHugeRange =
    end.diff(moment(startDate, API_DATE_FORMAT), "days") > MAX_FILLED_DAYS;
  const firstDataDate = isHugeRange
    ? dataList.reduce(
        (earliest, datum) => (datum.date < earliest ? datum.date : earliest),
        dataList[0]?.date ?? startDate,
      )
    : startDate;
  const cursor = moment(
    firstDataDate > startDate ? firstDataDate : startDate,
    API_DATE_FORMAT,
  );

  while (cursor.isSameOrBefore(end, "day")) {
    const date = cursor.format(API_DATE_FORMAT);
    filledList.push({ date, values: valuesByDate.get(date) ?? emptyValues });
    cursor.add(1, "day");
  }

  return filledList;
}

export function getTimelineGranularity(
  startDate: string,
  endDate: string,
): TimelineGranularity {
  const spanDayCount =
    moment(endDate, API_DATE_FORMAT).diff(
      moment(startDate, API_DATE_FORMAT),
      "days",
    ) + 1;

  if (spanDayCount <= MAX_DAY_GRANULARITY_DAYS) return "day";
  return spanDayCount <= MAX_WEEK_GRANULARITY_DAYS ? "week" : "month";
}

function getBucketDate(date: string, granularity: TimelineGranularity): string {
  const day = moment(date, API_DATE_FORMAT);
  if (granularity === "week") {
    return day.isoWeekday(1).format(API_DATE_FORMAT);
  }
  return granularity === "month" ? day.format(MONTH_KEY_FORMAT) : date;
}

export function bucketTimeline<TKey extends string>(
  dataList: readonly TimelineDatum<TKey>[],
  keyList: readonly TKey[],
  granularity: TimelineGranularity,
  aggregation: TimelineAggregation = "sum",
): TimelineDatum<TKey>[] {
  if (granularity === "day") return [...dataList];

  const bucketByDate = new Map<
    string,
    { totals: Record<TKey, number>; dayCount: number }
  >();
  dataList.forEach((datum) => {
    const bucketDate = getBucketDate(datum.date, granularity);
    const bucket = bucketByDate.get(bucketDate) ?? {
      totals: mapSeriesValues(keyList, () => 0),
      dayCount: 0,
    };
    keyList.forEach((key) => {
      bucket.totals[key] += datum.values[key];
    });
    bucket.dayCount += 1;
    bucketByDate.set(bucketDate, bucket);
  });

  return Array.from(bucketByDate, ([date, { totals, dayCount }]) => ({
    date,
    values:
      aggregation === "mean"
        ? mapSeriesValues(keyList, (key) => Math.round(totals[key] / dayCount))
        : totals,
  }));
}

export function keepWithinRange<TKey extends string>(
  dataList: readonly TimelineDatum<TKey>[],
  startDate: string,
  endDate: string,
): TimelineDatum<TKey>[] {
  return dataList
    .filter((datum) => datum.date >= startDate && datum.date <= endDate)
    .toSorted((first, second) => first.date.localeCompare(second.date));
}

export function toCumulative<TKey extends string>(
  dataList: readonly TimelineDatum<TKey>[],
  keyList: readonly TKey[],
): TimelineDatum<TKey>[] {
  const runningTotals = mapSeriesValues(keyList, () => 0);

  return dataList.map((datum) => {
    keyList.forEach((key) => {
      runningTotals[key] += datum.values[key];
    });
    return { date: datum.date, values: { ...runningTotals } };
  });
}

export function isRankedTruncated(rowCount: number, limit: number): boolean {
  return rowCount > limit + 1;
}

export function rankWithOthers<TKey extends string>(
  dataList: readonly RankedDatum<TKey>[],
  keyList: readonly TKey[],
  limit: number,
  othersLabel: string,
): RankedDatum<TKey>[] {
  const sortedList = dataList.toSorted(
    (first, second) =>
      sumSeriesValues(second.values, keyList) -
      sumSeriesValues(first.values, keyList),
  );

  if (!isRankedTruncated(sortedList.length, limit)) return sortedList;

  const restList = sortedList.slice(limit);
  return [
    ...sortedList.slice(0, limit),
    {
      label: othersLabel,
      values: mapSeriesValues(keyList, (key) =>
        restList.reduce((sum, datum) => sum + datum.values[key], 0),
      ),
    },
  ];
}

export function toChartConfig<TKey extends string>(
  seriesList: readonly ChartSeries<TKey>[],
): ChartConfig {
  return Object.fromEntries(
    seriesList.map((series) => [
      series.key,
      { label: series.label, color: series.color },
    ]),
  );
}

export function getChartCardStatus({
  isLoading,
  isError,
  isEmpty,
}: {
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
}): ChartCardStatus {
  if (isLoading) return "loading";
  if (isError) return "error";
  return isEmpty ? "empty" : "ready";
}

const countFormatterByLang = new Map<string, Intl.NumberFormat>();
const compactFormatterByLang = new Map<string, Intl.NumberFormat>();
const shareFormatterByLangAndPrecision = new Map<string, Intl.NumberFormat>();
const dayTickFormatterByLang = new Map<string, Intl.DateTimeFormat>();
const monthTickFormatterByLang = new Map<string, Intl.DateTimeFormat>();
const dayLabelFormatterByLang = new Map<string, Intl.DateTimeFormat>();
const monthLabelFormatterByLang = new Map<string, Intl.DateTimeFormat>();
const clockFormatterByLangAndPrecision = new Map<string, Intl.DateTimeFormat>();
const dayMonthFormatterByLang = new Map<string, Intl.DateTimeFormat>();

function getCached<TFormatter>(
  cache: Map<string, TFormatter>,
  lang: string,
  create: () => TFormatter,
): TFormatter {
  const cached = cache.get(lang);
  if (cached) return cached;

  const formatter = create();
  cache.set(lang, formatter);
  return formatter;
}

export function formatCount(value: number, lang: string): string {
  return getCached(
    countFormatterByLang,
    lang,
    () => new Intl.NumberFormat(lang),
  ).format(value);
}

export function formatCompactCount(value: number, lang: string): string {
  return getCached(
    compactFormatterByLang,
    lang,
    () =>
      new Intl.NumberFormat(lang, {
        notation: "compact",
        maximumFractionDigits: 1,
      }),
  ).format(value);
}

const BYTE_UNIT_LIST = [
  "megabyte",
  "gigabyte",
  "terabyte",
  "petabyte",
] as const;
const BYTES_PER_MEGABYTE = 1024 ** 2;
const BYTE_UNIT_SWITCH_THRESHOLD = 1000;
const AXIS_TARGET_INTERVAL_COUNT = 4;
const AXIS_STEP_FACTOR_LIST = [1, 2, 2.5, 5, 10];
const HOUR_AXIS_STEP_FACTOR_LIST = [1, 2, 5, 10];
const AXIS_MIN_STEP = 0.1;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const byteFormatterByLangAndUnit = new Map<string, Intl.NumberFormat>();

type ByteUnit = (typeof BYTE_UNIT_LIST)[number];

interface ByteScale {
  unit: ByteUnit;
  bytesPerUnit: number;
  value: number;
}

function roundToOneDecimal(value: number): number {
  return Math.round(value * 10) / 10;
}

// Switches unit once the label would read 1000 or more, so "1,024 MB" never shows.
function getByteScale(bytes: number): ByteScale {
  // Divides instead of using Math.log: log ratios land just under exact powers of 1024.
  let value = bytes / BYTES_PER_MEGABYTE;
  let exponent = 0;
  while (
    roundToOneDecimal(value) >= BYTE_UNIT_SWITCH_THRESHOLD &&
    exponent < BYTE_UNIT_LIST.length - 1
  ) {
    value /= 1024;
    exponent += 1;
  }
  return {
    unit: BYTE_UNIT_LIST[exponent] ?? "megabyte",
    bytesPerUnit: BYTES_PER_MEGABYTE * 1024 ** exponent,
    value,
  };
}

function formatByteValue(value: number, unit: ByteUnit, lang: string): string {
  return getCached(
    byteFormatterByLangAndUnit,
    `${lang}:${unit}`,
    () =>
      new Intl.NumberFormat(lang, {
        style: "unit",
        unit,
        maximumFractionDigits: 1,
      }),
  ).format(value);
}

export function formatBytes(bytes: number, lang: string): string {
  const { unit, value } = getByteScale(bytes);
  return formatByteValue(value, unit, lang);
}

function isMultipleOf(value: number, step: number): boolean {
  const ratio = value / step;
  return Math.abs(ratio - Math.round(ratio)) < 1e-9;
}

// A step is valid only if the axis labels can show it: a multiple of the unit's minimum step.
function getRoundTickList(
  maxValue: number,
  unitSize: number,
  minStep: number,
  factorList: readonly number[] = AXIS_STEP_FACTOR_LIST,
): number[] {
  const axisMax = maxValue > 0 ? maxValue : 1;
  const rawStep = axisMax / AXIS_TARGET_INTERVAL_COUNT;
  const magnitude = Math.max(10 ** Math.floor(Math.log10(rawStep)), minStep);
  const step =
    factorList
      .map((factor) => factor * magnitude)
      .filter((candidate) => isMultipleOf(candidate, minStep))
      .find((candidate) => candidate >= rawStep) ?? magnitude * 10;
  const tickCount = Math.max(1, Math.ceil(axisMax / step - 1e-9));

  return Array.from(
    { length: tickCount + 1 },
    (_, index) => Number((index * step).toFixed(1)) * unitSize,
  );
}

export function getByteAxis(maxBytes: number, lang: string): RoundAxis {
  const { unit, bytesPerUnit, value: maxValue } = getByteScale(maxBytes);

  return {
    tickList: getRoundTickList(maxValue, bytesPerUnit, AXIS_MIN_STEP),
    formatTick: (bytes) => formatByteValue(bytes / bytesPerUnit, unit, lang),
  };
}

// Under a minute the exact seconds are shown; beyond, values round to the minute.
export function formatDuration(seconds: number): string {
  const wholeSeconds = Math.round(seconds);
  const roundedSeconds =
    wholeSeconds < SECONDS_PER_MINUTE
      ? wholeSeconds
      : Math.round(seconds / SECONDS_PER_MINUTE) * SECONDS_PER_MINUTE;
  return (
    prettyDurations(roundedSeconds, {
      valueUnit: DURATION_UNITS.SECONDS,
      format: { noDays: true },
    }).humanised || `0${t("editor-utils:minuteUnit")}`
  );
}

function getDurationAxis(maxSeconds: number): RoundAxis {
  const tickList =
    maxSeconds >= SECONDS_PER_HOUR
      ? getRoundTickList(
          maxSeconds / SECONDS_PER_HOUR,
          SECONDS_PER_HOUR,
          AXIS_MIN_STEP,
          HOUR_AXIS_STEP_FACTOR_LIST,
        )
      : getRoundTickList(
          maxSeconds / SECONDS_PER_MINUTE,
          SECONDS_PER_MINUTE,
          1,
        );
  return { tickList, formatTick: formatDuration };
}

export function formatShare(
  ratio: number,
  lang: string,
  maximumFractionDigits = 0,
): string {
  return getCached(
    shareFormatterByLangAndPrecision,
    `${lang}:${maximumFractionDigits}`,
    () =>
      new Intl.NumberFormat(lang, {
        style: "percent",
        maximumFractionDigits,
      }),
  ).format(ratio);
}

export function formatRankedValue(
  total: number,
  grandTotal: number,
  lang: string,
): string {
  const ratio = grandTotal === 0 ? 0 : total / grandTotal;
  return `${formatCount(total, lang)} · ${formatShare(ratio, lang)}`;
}

function formatDate(
  cache: Map<string, Intl.DateTimeFormat>,
  options: Intl.DateTimeFormatOptions,
  date: string,
  lang: string,
): string {
  return getCached(
    cache,
    lang,
    () => new Intl.DateTimeFormat(lang, options),
  ).format(moment(date, API_DATE_FORMAT).toDate());
}

export function formatTimelineTick(
  date: string,
  granularity: TimelineGranularity,
  lang: string,
): string {
  return granularity === "month"
    ? formatDate(
        monthTickFormatterByLang,
        { month: "short", year: "2-digit" },
        date,
        lang,
      )
    : formatDate(
        dayTickFormatterByLang,
        { day: "numeric", month: "short" },
        date,
        lang,
      );
}

export function formatTimelineTooltipLabel(
  date: string,
  granularity: TimelineGranularity,
  lang: string,
  formatWeek: (formattedDate: string) => string,
): string {
  if (granularity === "month") {
    return formatDate(
      monthLabelFormatterByLang,
      { month: "long", year: "numeric" },
      date,
      lang,
    );
  }

  const dayLabel = formatDate(
    dayLabelFormatterByLang,
    { day: "numeric", month: "long", year: "numeric" },
    date,
    lang,
  );
  return granularity === "week" ? formatWeek(dayLabel) : dayLabel;
}

export function formatClockTime(
  timestamp: number,
  lang: string,
  withSeconds = false,
): string {
  return getCached(
    clockFormatterByLangAndPrecision,
    `${lang}:${withSeconds}`,
    () =>
      new Intl.DateTimeFormat(lang, {
        hour: "2-digit",
        minute: "2-digit",
        second: withSeconds ? "2-digit" : undefined,
        hourCycle: "h23",
      }),
  ).format(timestamp);
}

export function formatDayMonth(timestamp: number, lang: string): string {
  return getCached(
    dayMonthFormatterByLang,
    lang,
    () => new Intl.DateTimeFormat(lang, { day: "numeric", month: "short" }),
  ).format(timestamp);
}

export function truncateLabel(label: string, maxLength: number): string {
  return label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label;
}

export function getCategoryAxisWidth(
  labelList: readonly string[],
  maxLength: number,
): number {
  const longestLength = Math.max(
    0,
    ...labelList.map((label) => truncateLabel(label, maxLength).length),
  );
  const width =
    longestLength * CATEGORY_AXIS_CHAR_WIDTH + CATEGORY_AXIS_PADDING;

  return Math.min(
    CATEGORY_AXIS_MAX_WIDTH,
    Math.max(CATEGORY_AXIS_MIN_WIDTH, width),
  );
}

export function getValueAxisConfig(
  maxValue: number,
  lang: string,
  unit: ChartValueUnit = "count",
): ValueAxisConfig {
  if (unit === "count") {
    return {
      width: getCategoryAxisWidth(
        [formatCompactCount(maxValue, lang)],
        Number.POSITIVE_INFINITY,
      ),
      domain: [0, "auto"],
      formatTick: (value) => formatCompactCount(value, lang),
      formatValue: (value) => formatCount(value, lang),
    };
  }

  const { tickList, formatTick } =
    unit === "bytes" ? getByteAxis(maxValue, lang) : getDurationAxis(maxValue);
  return {
    width: getCategoryAxisWidth(
      tickList.map(formatTick),
      Number.POSITIVE_INFINITY,
    ),
    domain: [0, tickList.at(-1) ?? 0],
    tickList,
    formatTick,
    formatValue: (value) =>
      unit === "bytes" ? formatBytes(value, lang) : formatDuration(value),
  };
}
